import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from "../components/Sidebar";
import WordStyleEditor from "../components/WordStyleEditor";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ComboSearch } from "./cobros/ChequesQBPage";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { useAuth } from '../App';
import { 
  BookOpen, Plus, Edit, Trash2, Eye, Lock, Unlock, Download, Upload,
  History, CheckCircle, XCircle, Clock, FileText, Users, Settings,
  AlertTriangle, Search, Filter, ChevronRight, Save, RotateCcw,
  Send, Check, X, RefreshCw, Briefcase, Building, ShieldOff, GitCompare, FileCheck, Globe, Copy, ScrollText, Loader2, FileDown
} from "lucide-react";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

// Componente drag & drop para archivos DOCX
function DocxDropZone({ onFileSelected }) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [fileName, setFileName] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const processFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(docx|doc)$/i)) {
      toast.error("Solo se permiten archivos Word (.docx, .doc)");
      return;
    }
    setFileName(file.name);
    // Create a synthetic event for the existing handler
    const syntheticEvent = { target: { files: [file], value: '' } };
    onFileSelected(syntheticEvent);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    processFile(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
    e.target.value = '';
  };

  return (
    <div
      data-testid="docx-drop-zone"
      onClick={handleClick}
      onDragOver={handleDrag}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDrop={handleDrop}
      className={`
        relative cursor-pointer rounded-lg border-2 border-dashed transition-all duration-200
        ${isDragging 
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 scale-[1.01]' 
          : fileName 
            ? 'border-green-400 bg-green-50/50 dark:bg-green-950/20' 
            : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
        }
      `}
      style={{ padding: '24px 16px' }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".docx,.doc"
        onChange={handleFileInput}
        className="hidden"
      />
      <div className="flex flex-col items-center gap-2 text-center">
        {fileName ? (
          <>
            <FileText className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-400">{fileName}</p>
              <p className="text-xs text-slate-500 mt-0.5">Archivo cargado. Clic para reemplazar.</p>
            </div>
          </>
        ) : isDragging ? (
          <>
            <Upload className="h-8 w-8 text-blue-500 animate-bounce" />
            <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Suelta el archivo aquí</p>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 text-slate-400" />
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Arrastra un archivo Word aquí
              </p>
              <p className="text-xs text-slate-400 mt-0.5">o haz clic para seleccionar (.docx, .doc)</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Componente para mostrar diff de contenido HTML con cambios resaltados
// Diff a nivel de palabras que preserva las etiquetas HTML (vista tipo Word)
function htmlWordDiff(anterior, propuesto) {
  const tokenizar = (html) => (html || "").replace(/\s+/g, " ").match(/<[^>]+>| |[^<\s]+/g) || [];
  const ta = tokenizar(anterior);
  const tb = tokenizar(propuesto);
  const DiffMatchPatch = require('diff-match-patch');
  const dmp = new DiffMatchPatch();
  const { chars1, chars2, lineArray } = dmp.diff_linesToChars_(ta.join("\n") + "\n", tb.join("\n") + "\n");
  const diffs = dmp.diff_main(chars1, chars2, false);
  dmp.diff_charsToLines_(diffs, lineArray);

  const envolver = (tokens, cls) => {
    let s = "", buf = [];
    const flush = () => { if (buf.length) { s += `<span class="${cls}">${buf.join("")}</span>`; buf = []; } };
    for (const t of tokens) {
      if (t.startsWith("<")) {
        flush();
        if (cls === "diff-add") s += t; // etiquetas nuevas se conservan para mantener el formato
        // etiquetas eliminadas se omiten: el texto eliminado se muestra tachado en línea
      } else buf.push(t);
    }
    flush();
    return s;
  };

  let out = "";
  for (const [op, text] of diffs) {
    const tokens = text.split("\n").filter((t) => t !== "");
    if (op === 0) out += tokens.join("");
    else if (op === 1) out += envolver(tokens, "diff-add");
    else out += envolver(tokens, "diff-del");
  }
  return out;
}

// Vista tipo Word del documento con los cambios resaltados en línea
function DiffWordViewer({ contenidoAnterior, contenidoPropuesto }) {
  const html = React.useMemo(() => {
    try {
      return htmlWordDiff(contenidoAnterior, contenidoPropuesto);
    } catch (e) {
      console.error(e);
      return contenidoPropuesto || "";
    }
  }, [contenidoAnterior, contenidoPropuesto]);
  return (
    <div className="bg-slate-200/70 rounded-lg p-3 sm:p-6" data-testid="aprobacion-diff-word">
      <div className="bg-white shadow-md mx-auto rounded-sm px-8 sm:px-12 py-10" style={{ maxWidth: "820px", minHeight: "300px" }}>
        <div className="word-doc-content" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
      <style>{`
        .word-doc-content { font-family: 'Calibri', 'Arial', sans-serif; font-size: 11pt; line-height: 1.6; color: #1f2937; }
        .word-doc-content p { margin: 0 0 0.6em 0; }
        .word-doc-content h1 { font-size: 22pt; font-weight: bold; color: #193966; margin: 0.6em 0 0.4em; }
        .word-doc-content h2 { font-size: 16pt; font-weight: bold; color: #193966; margin: 0.6em 0 0.4em; }
        .word-doc-content h3 { font-size: 13pt; font-weight: bold; color: #1f3763; margin: 0.5em 0 0.3em; }
        .word-doc-content ul, .word-doc-content ol { padding-left: 1.6em; margin: 0.5em 0; }
        .word-doc-content li { margin: 0.25em 0; }
        .word-doc-content a { color: #0563c1; text-decoration: underline; }
        .word-doc-content img { max-width: 100%; height: auto; margin: 0.5em 0; }
        .word-doc-content blockquote { border-left: 4px solid #193966; padding-left: 1em; margin: 1em 0; color: #555; font-style: italic; }
        .word-doc-content hr { border: none; border-top: 1px solid #ccc; margin: 1em 0; }
        .word-doc-content mark { background-color: #ffff00; padding: 0 2px; }
        .word-doc-content table { border-collapse: collapse; width: 100%; margin: 0.8em 0; table-layout: auto; }
        .word-doc-content table td, .word-doc-content table th { border: 1px solid #999; padding: 6px 8px; vertical-align: top; min-width: 40px; font-size: 10pt; }
        .word-doc-content table th { font-weight: bold; background-color: #f0f0f0; }
        .word-doc-content table td p, .word-doc-content table th p { margin: 0; }
        .word-doc-content .diff-add { background-color: #dcfce7; color: #166534; border-radius: 2px; padding: 0 1px; }
        .word-doc-content .diff-del { background-color: #fee2e2; color: #991b1b; text-decoration: line-through; border-radius: 2px; padding: 0 1px; }
      `}</style>
    </div>
  );
}

function DiffViewer({ contenidoAnterior, contenidoPropuesto }) {
  const diffHtml = React.useMemo(() => {
    if (!contenidoAnterior && !contenidoPropuesto) return '';
    if (!contenidoAnterior) return contenidoPropuesto;
    if (!contenidoPropuesto) return '';

    try {
      // Use diff-match-patch library for efficient diffing
      const DiffMatchPatch = require('diff-match-patch');
      const dmp = new DiffMatchPatch();

      // Diff the raw HTML to preserve formatting structure
      const diffs = dmp.diff_main(contenidoAnterior, contenidoPropuesto);
      dmp.diff_cleanupSemantic(diffs);

      // Build HTML output - highlight only text, keep tags intact
      let html = '';
      for (const [op, text] of diffs) {
        if (op === 0) {
          // Equal - output as-is (preserves HTML structure)
          html += text;
        } else if (op === 1) {
          // Inserted text - highlight non-tag portions in yellow
          const parts = text.split(/(<[^>]*>)/);
          for (const part of parts) {
            if (part.startsWith('<')) {
              html += part; // HTML tag → output as-is
            } else if (part.length > 0) {
              html += `<span class="diff-add">${part}</span>`;
            }
          }
        } else if (op === -1) {
          // Deleted text - show text with strikethrough, skip tags
          const parts = text.split(/(<[^>]*>)/);
          for (const part of parts) {
            if (!part.startsWith('<') && part.length > 0) {
              html += `<span class="diff-del">${part}</span>`;
            }
            // Skip deleted HTML tags to avoid broken structure
          }
        }
      }

      return html;
    } catch (e) {
      console.error('DiffViewer error:', e);
      return contenidoPropuesto;
    }
  }, [contenidoAnterior, contenidoPropuesto]);

  return (
    <div className="border rounded-lg">
      <div className="px-4 py-2 bg-slate-50 border-b flex items-center justify-between">
        <h4 className="font-medium text-sm">Vista de cambios</h4>
        <span className="text-xs text-slate-500">Los cambios se resaltan en amarillo</span>
      </div>
      <div 
        className="diff-content p-4 prose prose-sm max-w-none overflow-auto max-h-[50vh]"
        style={{ fontFamily: "'Calibri', 'Arial', sans-serif", fontSize: '10pt' }}
        dangerouslySetInnerHTML={{ __html: diffHtml }}
      />
      <style>{`
        .diff-content .diff-add {
          background-color: #fef08a;
          padding: 1px 2px;
          border-radius: 2px;
        }
        .diff-content .diff-del {
          background-color: #fecaca;
          text-decoration: line-through;
          padding: 1px 2px;
          border-radius: 2px;
          opacity: 0.8;
        }
        .diff-content table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
        .diff-content td, .diff-content th { border: 1px solid #999; padding: 6px 8px; vertical-align: top; font-size: 10pt; }
        .diff-content th { font-weight: bold; background-color: #f0f0f0; }
      `}</style>
    </div>
  );
}

export default function ProcesosInternosPage() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("procesos");
  const [procesos, setProcesos] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [usuariosAprobadores, setUsuariosAprobadores] = useState([]);
  const [usuariosTodos, setUsuariosTodos] = useState([]);
  const [colaboradorSearch, setColaboradorSearch] = useState("");
  const [aprobacionesPendientes, setAprobacionesPendientes] = useState([]);
  const [configuracion, setConfiguracion] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Determinar si el usuario tiene permisos avanzados (Admin o Gestión Humana)
  const tienePermisosAvanzados = () => {
    if (!currentUser) return false;
    const role = (currentUser.role || "").toLowerCase();
    const deptoNombre = (currentUser.departamento_nombre || currentUser.department || "").toLowerCase();
    const isAdmin = currentUser.is_admin || role === "admin" || role === "administrador";
    const isGestionHumana = role === "gestion_humana" ||
                            deptoNombre.includes("gestion humana") || 
                            deptoNombre.includes("gestión humana") || 
                            deptoNombre === "rrhh" || 
                            deptoNombre === "recursos humanos";
    return isAdmin || isGestionHumana;
  };
  
  // Filtros
  const [filtroDepto, setFiltroDepto] = useState("all");
  const [filtroPuesto, setFiltroPuesto] = useState("all");
  const [filtroBusqueda, setFiltroBusqueda] = useState("");
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [versionesOpen, setVersionesOpen] = useState(false);
  const [aprobacionOpen, setAprobacionOpen] = useState(false);
  const [guardandoDoc, setGuardandoDoc] = useState(false);
  const [diffVersionOpen, setDiffVersionOpen] = useState(false);
  const [diffVersionData, setDiffVersionData] = useState(null);
  
  // Estado del proceso actual
  const [procesoActual, setProcesoActual] = useState(null);
  const [historialActual, setHistorialActual] = useState([]);
  const [versionesActual, setVersionesActual] = useState([]);
  const [aprobacionActual, setAprobacionActual] = useState(null);
  
  // Formulario
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    departamento_id: '',
    puesto: '',
    contenido: '',
    header_html: '',
    footer_html: '',
    aprobador_id: '',
    colaboradores: [],
    requiere_aprobacion: false
  });
  
  const [comentarioCambio, setComentarioCambio] = useState('');
  const [comentarioAprobacion, setComentarioAprobacion] = useState('');

  // Cargar datos
  const cargarDatos = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [procesosRes, puestosRes, aprobadoresRes, configRes, aprobacionesRes, usuariosRes] = await Promise.all([
        axios.get(`${API_URL}/api/procesos-internos`, { headers }),
        axios.get(`${API_URL}/api/procesos-internos/puestos`, { headers }),
        axios.get(`${API_URL}/api/procesos-internos/usuarios-aprobadores`, { headers }),
        axios.get(`${API_URL}/api/procesos-internos/configuracion`, { headers }),
        axios.get(`${API_URL}/api/procesos-internos/aprobaciones/pendientes`, { headers }),
        axios.get(`${API_URL}/api/users`, { headers })
      ]);
      
      setProcesos(procesosRes.data.procesos || []);
      setDepartamentos(procesosRes.data.departamentos || []);
      setPuestos(puestosRes.data || []);
      setUsuariosAprobadores(aprobadoresRes.data || []);
      setConfiguracion(configRes.data || {});
      setAprobacionesPendientes(aprobacionesRes.data || []);
      setUsuariosTodos(Array.isArray(usuariosRes.data) ? usuariosRes.data : (usuariosRes.data?.users || []));
    } catch (error) {
      toast.error("Error al cargar datos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Filtrar procesos
  const procesosFiltrados = procesos.filter(p => {
    if (filtroDepto !== "all" && p.departamento_id !== filtroDepto) return false;
    if (filtroPuesto !== "all" && p.puesto !== filtroPuesto) return false;
    if (filtroBusqueda && !p.titulo.toLowerCase().includes(filtroBusqueda.toLowerCase())) return false;
    if (p.estado === "archivado") return false;
    return true;
  });

  // Handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCrear = () => {
    setProcesoActual(null);
    setFormData({
      titulo: '',
      descripcion: '',
      departamento_id: '',
      puesto: '',
      contenido: '',
      header_html: '',
      footer_html: '',
      aprobador_id: '',
      colaboradores: [],
      requiere_aprobacion: false
    });
    setColaboradorSearch("");
    setDialogOpen(true);
  };

  const handleEditar = (proceso) => {
    setProcesoActual(proceso);
    setFormData({
      titulo: proceso.titulo || '',
      descripcion: proceso.descripcion || '',
      departamento_id: proceso.departamento_id || '',
      puesto: proceso.puesto || '',
      contenido: proceso.contenido || '',
      header_html: proceso.header_html || '',
      footer_html: proceso.footer_html || '',
      aprobador_id: proceso.aprobador_id || '',
      colaboradores: proceso.colaboradores || [],
      requiere_aprobacion: proceso.requiere_aprobacion || false
    });
    setColaboradorSearch("");
    setDialogOpen(true);
  };

  const handleClonar = (proceso) => {
    // Clona depto, aprobador, colaboradores y formato; solo queda editar título/puesto y cargar el documento
    setProcesoActual(null);
    setFormData({
      titulo: `${proceso.titulo || ''} (Copia)`,
      descripcion: proceso.descripcion || '',
      departamento_id: proceso.departamento_id || '',
      puesto: proceso.puesto || '',
      contenido: '',
      header_html: proceso.header_html || '',
      footer_html: proceso.footer_html || '',
      aprobador_id: proceso.aprobador_id || '',
      colaboradores: proceso.colaboradores || [],
      requiere_aprobacion: proceso.requiere_aprobacion || false
    });
    setColaboradorSearch("");
    setDialogOpen(true);
    toast.info("Proceso clonado: edite el título y el puesto, y cargue el documento");
  };

  const handleGuardar = async () => {
    if (!formData.titulo || !formData.departamento_id) {
      toast.error("Título y departamento son requeridos");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const dataToSend = {
        ...formData,
        departamento_id: formData.departamento_id,
        puesto: formData.puesto || null,
        aprobador_id: formData.aprobador_id || null
      };

      if (procesoActual) {
        await axios.put(`${API_URL}/api/procesos-internos/${procesoActual.id}`, dataToSend, { headers });
        toast.success("Proceso actualizado");
      } else {
        await axios.post(`${API_URL}/api/procesos-internos`, dataToSend, { headers });
        toast.success("Proceso creado");
      }

      setDialogOpen(false);
      cargarDatos();
    } catch (error) {
      toast.error("Error al guardar");
      console.error(error);
    }
  };

  const handleEliminar = async (proceso) => {
    if (!window.confirm(`¿Estás seguro de archivar "${proceso.titulo}"?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/procesos-internos/${proceso.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Proceso archivado");
      cargarDatos();
    } catch (error) {
      toast.error("Error al archivar");
    }
  };

  const handleCheckout = async (proceso) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/procesos-internos/${proceso.id}/checkout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Documento reservado para edición");
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reservar");
    }
  };

  const handleCheckin = async (proceso, crearVersion = true) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/procesos-internos/${proceso.id}/checkin?crear_version=${crearVersion}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(crearVersion ? "Documento liberado y versión creada" : "Documento liberado");
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al liberar");
    }
  };

  const handlePublicar = async (proceso) => {
    if (proceso.tiene_aprobacion_pendiente) {
      toast.warning("Este proceso tiene cambios pendientes de aprobación. Apruébalos antes de publicar.");
      return;
    }
    if (!window.confirm(`¿Publicar "${proceso.titulo}"? Quedará visible para sus colaboradores en el Portal de Procedimientos.`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/procesos-internos/${proceso.id}/publicar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Procedimiento publicado");
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al publicar");
    }
  };

  const handleForzarLiberacion = async (proceso) => {
    const nombre = proceso.checkout_user_nombre || "otro usuario";
    if (!window.confirm(`¿Desbloquear "${proceso.titulo}"? Se quitará la reserva de ${nombre}.`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/procesos-internos/${proceso.id}/forzar-liberacion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Proceso desbloqueado");
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desbloquear");
    }
  };

  const handleAbrirEditor = async (proceso) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/procesos-internos/${proceso.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProcesoActual(response.data);
      setFormData(prev => ({ 
        ...prev, 
        contenido: response.data.contenido || '',
        header_html: response.data.header_html || '',
        footer_html: response.data.footer_html || ''
      }));
      setComentarioCambio('');
      setEditorOpen(true);
    } catch (error) {
      toast.error("Error al cargar proceso");
    }
  };

  const handleGuardarContenido = async () => {
    if (guardandoDoc) return;
    setGuardandoDoc(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_URL}/api/procesos-internos/${procesoActual.id}`,
        { 
          contenido: formData.contenido,
          header_html: formData.header_html,
          footer_html: formData.footer_html,
          comentario_cambio: comentarioCambio 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.requiere_aprobacion) {
        toast.success("Cambios enviados para aprobación");
      } else {
        toast.success("Contenido guardado");
      }
      
      setEditorOpen(false);
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setGuardandoDoc(false);
    }
  };

  const handleVerHistorial = async (proceso) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/procesos-internos/${proceso.id}/trazabilidad`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorialActual(response.data);
      setProcesoActual(proceso);
      setHistorialOpen(true);
    } catch (error) {
      toast.error("Error al cargar historial");
    }
  };

  const handleExportarTrazabilidad = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/procesos-internos/${procesoActual.id}/trazabilidad/excel`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Trazabilidad_${(procesoActual.titulo || 'proceso').replace(/[^\w-]+/g, '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (error) {
      toast.error("Error al exportar la trazabilidad");
    }
  };

  const handleVerVersiones = async (proceso) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/procesos-internos/${proceso.id}/versiones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersionesActual(response.data);
      setProcesoActual(proceso);
      setVersionesOpen(true);
    } catch (error) {
      toast.error("Error al cargar versiones");
    }
  };

  const handleRestaurarVersion = async (version) => {
    if (!window.confirm(`¿Restaurar a la versión ${version.version}?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/procesos-internos/${procesoActual.id}/restaurar/${version.version}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Versión ${version.version} restaurada`);
      setVersionesOpen(false);
      cargarDatos();
    } catch (error) {
      toast.error("Error al restaurar");
    }
  };

  const handleExportarWord = async (proceso) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/procesos-internos/${proceso.id}/exportar-word`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${proceso.titulo.replace(/\s+/g, '_')}_v${proceso.version}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Documento exportado");
    } catch (error) {
      toast.error("Error al exportar");
    }
  };

  const handleImportarWord = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem("token");
      toast.loading("Importando documento...", { id: 'import-word' });
      const response = await axios.post(
        `${API_URL}/api/procesos-internos/importar-word`,
        formDataUpload,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      setFormData(prev => ({ 
        ...prev, 
        contenido: response.data.contenido_html,
        header_html: response.data.header_html || '',
        footer_html: response.data.footer_html || '',
        titulo: prev.titulo || file.name.replace(/\.(docx|doc)$/i, '').replace(/_/g, ' ')
      }));
      
      const paginas = response.data.paginas || 1;
      const warnings = response.data.mensajes?.length || 0;
      toast.success(`Documento importado (${paginas} páginas)${warnings > 0 ? ` - ${warnings} advertencias` : ''}`, { id: 'import-word' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al importar documento", { id: 'import-word' });
      console.error(error);
    }
    e.target.value = '';
  };

  const handleRevisarModificaciones = async (proceso) => {
    const local = aprobacionesPendientes.find(a => a.proceso_id === proceso.id);
    if (local) { setAprobacionActual(local); setAprobacionOpen(true); return; }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/procesos-internos/${proceso.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ap = res.data?.aprobacion_pendiente;
      if (ap) {
        setAprobacionActual(ap);
        setAprobacionOpen(true);
      } else {
        toast.info("No hay modificaciones pendientes de revisión");
        cargarDatos();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cargar las modificaciones");
    }
  };

  const handleProcesarAprobacion = async (aprobacion, accion) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/procesos-internos/${aprobacion.proceso_id}/aprobaciones/${aprobacion.id}`,
        { accion, comentario: comentarioAprobacion },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(accion === 'aprobar' ? "Cambio aprobado" : "Cambio rechazado");
      setAprobacionOpen(false);
      setComentarioAprobacion('');
      cargarDatos();
    } catch (error) {
      toast.error("Error al procesar");
    }
  };

  const handleGuardarConfiguracion = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/procesos-internos/configuracion`,
        configuracion,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    }
  };

  const getEstadoBadge = (proceso) => {
    if (proceso.checkout_by) {
      return <Badge variant="warning" className="gap-1 bg-blue-100 text-blue-800"><Edit className="h-3 w-3" /> En edición{proceso.checkout_user_nombre ? `: ${proceso.checkout_user_nombre}` : ''}</Badge>;
    }
    if (proceso.tiene_aprobacion_pendiente) {
      return <Badge variant="warning" className="gap-1 bg-amber-100 text-amber-800"><Clock className="h-3 w-3" /> Pendiente de Aprobación</Badge>;
    }
    switch (proceso.estado) {
      case 'publicado':
        return <Badge variant="default" className="gap-1 bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" /> Publicado</Badge>;
      case 'borrador':
        return <Badge variant="secondary" className="gap-1"><FileText className="h-3 w-3" /> Borrador</Badge>;
      case 'pendiente':
        return <Badge variant="warning" className="gap-1 bg-amber-100 text-amber-800"><Clock className="h-3 w-3" /> Pendiente de Aprobación</Badge>;
      default:
        return <Badge variant="outline">{proceso.estado}</Badge>;
    }
  };

  const getNombreDepartamento = (id) => {
    const depto = departamentos.find(d => d.id === id || d.name === id || d.nombre === id);
    return depto?.name || depto?.nombre || id || 'Sin departamento';
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      creacion: 'Creación',
      edicion: 'Edición',
      checkout: 'Reservado',
      checkin: 'Liberado',
      version: 'Nueva versión',
      aprobacion: 'Aprobado',
      rechazo: 'Rechazado',
      publicacion: 'Publicado',
      restauracion: 'Restaurado',
      archivado: 'Archivado',
      liberacion_forzada: 'Liberación forzada'
    };
    return labels[tipo] || tipo;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 lg:p-8 overflow-auto" data-testid="procesos-internos-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-3">
                <BookOpen className="h-8 w-8 text-emerald-600" />
                Procesos Internos
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Gestión documental de procedimientos por departamento y puesto
              </p>
            </div>
            {aprobacionesPendientes.length > 0 && (
              <Badge variant="destructive" className="text-base px-4 py-2">
                {aprobacionesPendientes.length} aprobaciones pendientes
              </Badge>
            )}
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className={`grid w-full max-w-md ${tienePermisosAvanzados() ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <TabsTrigger value="procesos" className="gap-2">
                <FileText className="h-4 w-4" />
                Procesos
              </TabsTrigger>
              {tienePermisosAvanzados() && (
                <TabsTrigger value="configuracion" className="gap-2">
                  <Settings className="h-4 w-4" />
                  Configuración
                </TabsTrigger>
              )}
            </TabsList>

            {/* TAB: PROCESOS */}
            <TabsContent value="procesos" className="space-y-6">
              {/* Barra de acciones */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    {/* Nuevo Proceso - Solo Admin y Gestión Humana */}
                    {tienePermisosAvanzados() && (
                      <Button onClick={handleCrear} className="bg-emerald-600 hover:bg-emerald-700">
                        <Plus className="h-4 w-4 mr-2" />
                        Nuevo Proceso
                      </Button>
                    )}
                    
                    <div className="flex-1 flex flex-wrap items-center gap-3">
                      <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Buscar proceso..."
                          value={filtroBusqueda}
                          onChange={(e) => setFiltroBusqueda(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      
                      <Select value={filtroDepto} onValueChange={setFiltroDepto}>
                        <SelectTrigger className="w-48">
                          <Building className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Departamento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los departamentos</SelectItem>
                          {departamentos.map(d => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.name || d.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Select value={filtroPuesto} onValueChange={setFiltroPuesto}>
                        <SelectTrigger className="w-48">
                          <Briefcase className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Puesto" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los puestos</SelectItem>
                          {puestos.map(p => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Aprobaciones pendientes */}
              {aprobacionesPendientes.length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Aprobaciones Pendientes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {aprobacionesPendientes.map(ap => (
                        <div key={ap.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                          <div>
                            <p className="font-medium">{ap.proceso_titulo}</p>
                            <p className="text-sm text-slate-500">
                              Solicitado por {ap.solicitante_nombre} • {new Date(ap.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => { setAprobacionActual(ap); setAprobacionOpen(true); }}
                          >
                            Revisar
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Lista de procesos */}
              <Card>
                <CardHeader>
                  <CardTitle>Lista de Procesos</CardTitle>
                  <CardDescription>
                    {procesosFiltrados.length} procesos encontrados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                  ) : procesosFiltrados.length === 0 ? (
                    <div className="text-center py-12">
                      <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">No hay procesos disponibles</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Título</TableHead>
                          <TableHead>Departamento</TableHead>
                          <TableHead>Puesto</TableHead>
                          <TableHead>Versión</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Última modificación</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {procesosFiltrados.map(proceso => (
                          <TableRow key={proceso.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{proceso.titulo}</p>
                                {proceso.descripcion && (
                                  <p className="text-sm text-slate-500 truncate max-w-xs">{proceso.descripcion}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{getNombreDepartamento(proceso.departamento_id)}</TableCell>
                            <TableCell>{proceso.puesto || <span className="text-slate-400">General</span>}</TableCell>
                            <TableCell>
                              <Badge variant="outline">v{proceso.version || 1}</Badge>
                            </TableCell>
                            <TableCell>{getEstadoBadge(proceso)}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p>{proceso.updated_by_nombre}</p>
                                <p className="text-slate-500">{new Date(proceso.updated_at).toLocaleString()}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {/* Checkout/Checkin - Solo Admin y Gestión Humana */}
                                {tienePermisosAvanzados() && (
                                  <>
                                    {!proceso.checkout_by ? (
                                      <Button variant="ghost" size="icon" onClick={() => handleCheckout(proceso)} title="Reservar para edición" data-testid={`reservar-btn-${proceso.id}`}>
                                        <Lock className="h-4 w-4" />
                                      </Button>
                                    ) : proceso.puede_editar ? (
                                      <Button variant="ghost" size="icon" onClick={() => handleCheckin(proceso)} title="Liberar documento" data-testid={`liberar-btn-${proceso.id}`}>
                                        <Unlock className="h-4 w-4 text-green-600" />
                                      </Button>
                                    ) : null}
                                    {/* Desbloquear (liberación forzada) cuando está reservado */}
                                    {proceso.checkout_by && (
                                      <Button variant="ghost" size="icon" onClick={() => handleForzarLiberacion(proceso)} title={`Desbloquear proceso${proceso.checkout_user_nombre ? ` (reservado por ${proceso.checkout_user_nombre})` : ''}`} className="text-amber-600" data-testid={`desbloquear-btn-${proceso.id}`}>
                                        <ShieldOff className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                                {/* Ver/Editar - Todos los usuarios */}
                                <Button variant="ghost" size="icon" onClick={() => handleAbrirEditor(proceso)} title="Ver/Editar">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {/* Versiones - Solo Admin y Gestión Humana */}
                                {tienePermisosAvanzados() && (
                                  <Button variant="ghost" size="icon" onClick={() => handleVerVersiones(proceso)} title="Versiones">
                                    <History className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Trazabilidad completa - Solo Admin y Gestión Humana */}
                                {tienePermisosAvanzados() && (
                                  <Button variant="ghost" size="icon" onClick={() => handleVerHistorial(proceso)} title="Trazabilidad" data-testid={`trazabilidad-proceso-${proceso.id}`}>
                                    <ScrollText className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Revisar modificaciones pendientes (solo si hay aprobación pendiente) */}
                                {proceso.tiene_aprobacion_pendiente && (proceso.aprobador_id === currentUser?.id || tienePermisosAvanzados()) && (
                                  <Button variant="ghost" size="icon" onClick={() => handleRevisarModificaciones(proceso)} title="Revisar modificaciones pendientes" className="text-amber-600 relative" data-testid={`revisar-mod-btn-${proceso.id}`}>
                                    <FileCheck className="h-4 w-4" />
                                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-500"></span>
                                  </Button>
                                )}
                                {/* Publicar - Solo Admin y Gestión Humana, si no está publicado */}
                                {tienePermisosAvanzados() && proceso.estado !== 'publicado' && (
                                  <Button variant="ghost" size="icon" onClick={() => handlePublicar(proceso)} title="Publicar procedimiento (visible en el Portal)" className="text-[#80BC00]" data-testid={`publicar-btn-${proceso.id}`}>
                                    <Globe className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Exportar Word - Solo Admin y Gestión Humana */}
                                {tienePermisosAvanzados() && (
                                  <Button variant="ghost" size="icon" onClick={() => handleExportarWord(proceso)} title="Exportar Word">
                                    <Download className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Clonar - Solo Admin y Gestión Humana */}
                                {tienePermisosAvanzados() && (
                                  <Button variant="ghost" size="icon" onClick={() => handleClonar(proceso)} title="Clonar proceso" data-testid={`clonar-proceso-${proceso.id}`}>
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Configurar - Solo Admin y Gestión Humana */}
                                {tienePermisosAvanzados() && (
                                  <Button variant="ghost" size="icon" onClick={() => handleEditar(proceso)} title="Configurar">
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Eliminar/Archivar - Solo Admin y Gestión Humana */}
                                {tienePermisosAvanzados() && (
                                  <Button variant="ghost" size="icon" onClick={() => handleEliminar(proceso)} title="Archivar" className="text-red-500">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: CONFIGURACIÓN */}
            <TabsContent value="configuracion" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Notificaciones */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Notificaciones
                    </CardTitle>
                    <CardDescription>
                      Configura las notificaciones automáticas del módulo
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Solicitud de aprobación</p>
                        <p className="text-sm text-slate-500">Notificar al aprobador cuando hay cambios pendientes</p>
                      </div>
                      <Switch
                        checked={configuracion.notificar_solicitud_aprobacion ?? true}
                        onCheckedChange={(v) => setConfiguracion(c => ({...c, notificar_solicitud_aprobacion: v}))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Aprobación</p>
                        <p className="text-sm text-slate-500">Notificar cuando un cambio es aprobado</p>
                      </div>
                      <Switch
                        checked={configuracion.notificar_aprobacion ?? true}
                        onCheckedChange={(v) => setConfiguracion(c => ({...c, notificar_aprobacion: v}))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Rechazo</p>
                        <p className="text-sm text-slate-500">Notificar cuando un cambio es rechazado</p>
                      </div>
                      <Switch
                        checked={configuracion.notificar_rechazo ?? true}
                        onCheckedChange={(v) => setConfiguracion(c => ({...c, notificar_rechazo: v}))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Nueva versión</p>
                        <p className="text-sm text-slate-500">Notificar a usuarios del departamento sobre nuevas versiones</p>
                      </div>
                      <Switch
                        checked={configuracion.notificar_nueva_version ?? true}
                        onCheckedChange={(v) => setConfiguracion(c => ({...c, notificar_nueva_version: v}))}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Acceso y permisos */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Acceso y Permisos
                    </CardTitle>
                    <CardDescription>
                      Configura las reglas de acceso al módulo
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Acceso solo a departamento</p>
                        <p className="text-sm text-slate-500">Los usuarios solo ven procesos de su departamento</p>
                      </div>
                      <Switch
                        checked={configuracion.acceso_solo_departamento ?? true}
                        onCheckedChange={(v) => setConfiguracion(c => ({...c, acceso_solo_departamento: v}))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Edición sin reserva</p>
                        <p className="text-sm text-slate-500">Permitir editar sin hacer checkout previo</p>
                      </div>
                      <Switch
                        checked={configuracion.permitir_edicion_sin_checkout ?? false}
                        onCheckedChange={(v) => setConfiguracion(c => ({...c, permitir_edicion_sin_checkout: v}))}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Trazabilidad global */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="h-5 w-5" />
                      Trazabilidad Global
                    </CardTitle>
                    <CardDescription>
                      Historial reciente de cambios en todos los procesos
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <TrazabilidadGlobal />
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleGuardarConfiguracion} className="bg-emerald-600 hover:bg-emerald-700">
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Configuración
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Dialog: Crear/Editar Proceso */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{procesoActual ? "Editar Proceso" : "Nuevo Proceso"}</DialogTitle>
              <DialogDescription>
                Configura los datos básicos del proceso interno
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Título *</Label>
                  <Input
                    value={formData.titulo}
                    onChange={(e) => handleInputChange('titulo', e.target.value)}
                    placeholder="Ej: Procedimiento de Onboarding"
                  />
                </div>
                
                <div className="space-y-2 col-span-2">
                  <Label>Descripción</Label>
                  <Textarea
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    placeholder="Breve descripción del proceso..."
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Departamento *</Label>
                  <Select value={formData.departamento_id} onValueChange={(v) => handleInputChange('departamento_id', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar departamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {departamentos.map(d => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name || d.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Puesto específico</Label>
                  <Input
                    value={formData.puesto}
                    onChange={(e) => handleInputChange('puesto', e.target.value)}
                    placeholder="Ej: Analista de RRHH"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={formData.requiere_aprobacion}
                      onCheckedChange={(v) => handleInputChange('requiere_aprobacion', v)}
                    />
                    <Label>Requiere aprobación para cambios</Label>
                  </div>
                </div>

                {formData.requiere_aprobacion && (
                  <div className="space-y-2 col-span-2">
                    <Label>Usuario aprobador</Label>
                    <ComboSearch
                      value={formData.aprobador_id}
                      onChange={(v) => handleInputChange('aprobador_id', v)}
                      opciones={usuariosAprobadores.map(u => ({ value: u.id, label: `${u.nombre}${u.cargo ? ` — ${u.cargo}` : ''}` }))}
                      placeholder="Seleccionar aprobador"
                      testid="aprobador-combo"
                    />
                  </div>
                )}

                {/* Colaboradores: usuarios enlazados que consultan el proceso en el Portal */}
                <div className="space-y-2 col-span-2" data-testid="colaboradores-selector">
                  <Label>Colaboradores (consultan este proceso en el Portal)</Label>
                  <p className="text-xs text-slate-500">Selecciona los usuarios vinculados a este cargo/proceso. Solo ellos lo verán en el Portal de Procedimientos.</p>
                  <Input
                    placeholder="Buscar usuario por nombre o correo..."
                    value={colaboradorSearch}
                    onChange={(e) => setColaboradorSearch(e.target.value)}
                    data-testid="colaborador-search-input"
                  />
                  <div className="max-h-44 overflow-y-auto border rounded-md divide-y">
                    {usuariosTodos
                      .filter(u => {
                        const t = colaboradorSearch.toLowerCase();
                        return !t || u.name?.toLowerCase().includes(t) || u.email?.toLowerCase().includes(t);
                      })
                      .slice(0, 100)
                      .map(u => {
                        const sel = (formData.colaboradores || []).includes(u.id);
                        return (
                          <label key={u.id} className={`flex items-center gap-2 p-2 cursor-pointer ${sel ? 'bg-emerald-50' : 'hover:bg-slate-50'}`} data-testid={`colaborador-option-${u.id}`}>
                            <Checkbox
                              checked={sel}
                              onCheckedChange={(checked) => {
                                const current = formData.colaboradores || [];
                                handleInputChange('colaboradores', checked ? [...current, u.id] : current.filter(id => id !== u.id));
                              }}
                            />
                            <span className="text-sm flex-1">{u.name} <span className="text-slate-400">· {u.email}</span></span>
                          </label>
                        );
                      })}
                    {usuariosTodos.length === 0 && <p className="text-xs text-slate-400 p-2">Cargando usuarios…</p>}
                  </div>
                  {(formData.colaboradores || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {(formData.colaboradores || []).map(uid => {
                        const u = usuariosTodos.find(x => x.id === uid);
                        return (
                          <Badge key={uid} variant="secondary" className="flex items-center gap-1">
                            {u?.name || uid}
                            <X className="h-3 w-3 cursor-pointer hover:text-red-500" onClick={() => handleInputChange('colaboradores', (formData.colaboradores || []).filter(id => id !== uid))} />
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>

                {!procesoActual && (
                  <div className="space-y-2 col-span-2">
                    <Label>Importar desde Word (opcional)</Label>
                    <DocxDropZone onFileSelected={handleImportarWord} />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleGuardar} className="bg-emerald-600 hover:bg-emerald-700">
                {procesoActual ? "Guardar cambios" : "Crear proceso"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Editor de contenido - Estilo Word */}
        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          <DialogContent className="max-w-[95vw] w-[1200px] h-[90vh] overflow-hidden flex flex-col p-0">
            <DialogHeader className="px-4 py-3 border-b bg-slate-50 dark:bg-slate-800">
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {procesoActual?.titulo}
                <Badge variant="outline" className="ml-2">v{procesoActual?.version || 1}</Badge>
                {procesoActual?.checkout_by && !procesoActual?.puede_editar && (
                  <Badge variant="destructive" className="ml-2">
                    <Lock className="h-3 w-3 mr-1" />
                    Bloqueado por {procesoActual.checkout_user_nombre}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-hidden">
              <WordStyleEditor
                content={formData.contenido}
                onChange={(html) => handleInputChange('contenido', html)}
                editable={!procesoActual?.checkout_by || procesoActual?.puede_editar}
                minHeight="100%"
                headerHtml={formData.header_html}
                footerHtml={formData.footer_html}
              />
            </div>

            {(!procesoActual?.checkout_by || procesoActual?.puede_editar) && (
              <div className="px-4 py-3 border-t bg-slate-50 dark:bg-slate-800 space-y-3">
                <div className="flex items-center gap-3">
                  <Label className="whitespace-nowrap">Comentario del cambio:</Label>
                  <Input
                    value={comentarioCambio}
                    onChange={(e) => setComentarioCambio(e.target.value)}
                    placeholder="Describe los cambios realizados..."
                    className="flex-1"
                  />
                </div>
                
                <DialogFooter className="sm:justify-between">
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditorOpen(false)}>
                      <X className="h-4 w-4 mr-2" />
                      Cerrar
                    </Button>
                    <Button onClick={() => handleVerHistorial(procesoActual)} variant="outline">
                      <History className="h-4 w-4 mr-2" />
                      Historial
                    </Button>
                    <label>
                      <Button variant="outline" asChild>
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          Importar Word
                        </span>
                      </Button>
                      <input
                        type="file"
                        accept=".docx,.doc"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append('file', file);
                          try {
                            const token = localStorage.getItem("token");
                            toast.loading("Importando...", { id: 'import-editor' });
                            const res = await axios.post(
                              `${API_URL}/api/procesos-internos/importar-word`, fd,
                              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
                            );
                            setFormData(prev => ({
                              ...prev,
                              contenido: res.data.contenido_html,
                              header_html: res.data.header_html || '',
                              footer_html: res.data.footer_html || ''
                            }));
                            toast.success(`Documento importado (${res.data.paginas || 1} páginas)`, { id: 'import-editor' });
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Error al importar", { id: 'import-editor' });
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <Button onClick={handleGuardarContenido} disabled={guardandoDoc} className="bg-blue-600 hover:bg-blue-700">
                    {guardandoDoc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Guardar documento
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog: Versiones */}
        <Dialog open={versionesOpen} onOpenChange={setVersionesOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Historial de Versiones</DialogTitle>
              <DialogDescription>{procesoActual?.titulo}</DialogDescription>
            </DialogHeader>

            <ScrollArea className="h-96">
              {versionesActual.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No hay versiones anteriores</p>
              ) : (
                <div className="space-y-3">
                  {versionesActual.map(v => (
                    <div key={v.id} className="p-4 border rounded-lg" data-testid={`version-row-${v.version}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">Versión {v.version}</Badge>
                            <span className="text-sm text-slate-500">
                              {new Date(v.snapshot_at || v.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            Por {v.created_by_nombre}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!v.es_inicial && (
                            <Button variant="outline" size="sm" onClick={() => { setDiffVersionData(v); setDiffVersionOpen(true); }} title="Ver cambios (control de cambios)" data-testid={`ver-cambios-btn-${v.version}`}>
                              <GitCompare className="h-4 w-4 mr-2" />
                              Ver cambios
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => handleRestaurarVersion(v)}>
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Restaurar
                          </Button>
                        </div>
                      </div>

                      {/* Columna de cambios: resumen rápido agregado / eliminado */}
                      {v.es_inicial ? (
                        <p className="text-xs text-slate-400 mt-3 italic">Versión inicial</p>
                      ) : (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`cambios-resumen-${v.version}`}>
                          <div>
                            <p className="text-xs font-semibold text-green-700 mb-1">Agregado</p>
                            {(v.cambios?.agregado?.length > 0) ? (
                              <ul className="space-y-1">
                                {v.cambios.agregado.slice(0, 8).map((t, i) => (
                                  <li key={i} className="text-xs text-green-800 bg-green-50 rounded px-2 py-1 break-words">+ {t}</li>
                                ))}
                                {v.cambios.agregado.length > 8 && (
                                  <li className="text-xs text-slate-400">+{v.cambios.agregado.length - 8} más…</li>
                                )}
                              </ul>
                            ) : <p className="text-xs text-slate-400">—</p>}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-red-700 mb-1">Eliminado</p>
                            {(v.cambios?.eliminado?.length > 0) ? (
                              <ul className="space-y-1">
                                {v.cambios.eliminado.slice(0, 8).map((t, i) => (
                                  <li key={i} className="text-xs text-red-800 bg-red-50 rounded px-2 py-1 line-through break-words">− {t}</li>
                                ))}
                                {v.cambios.eliminado.length > 8 && (
                                  <li className="text-xs text-slate-400">+{v.cambios.eliminado.length - 8} más…</li>
                                )}
                              </ul>
                            ) : <p className="text-xs text-slate-400">—</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <DialogFooter>
              <Button variant="outline" onClick={() => setVersionesOpen(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Control de cambios (texto completo) de una versión */}
        <Dialog open={diffVersionOpen} onOpenChange={setDiffVersionOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Cambios — Versión {diffVersionData?.version}</DialogTitle>
              <DialogDescription>
                Comparación con la versión anterior. Lo nuevo se resalta en verde y lo eliminado en rojo (tachado).
              </DialogDescription>
            </DialogHeader>
            {diffVersionData && (
              <div className="space-y-1 text-sm" data-testid="diff-lineas-view">
                {(diffVersionData.diff_lineas || []).length === 0 ? (
                  <p className="text-slate-400 italic">Sin cambios de texto detectados.</p>
                ) : (
                  diffVersionData.diff_lineas.map((ln, i) => (
                    <div
                      key={i}
                      className={
                        ln.tipo === "add"
                          ? "bg-green-50 text-green-800 rounded px-2 py-1 border-l-2 border-green-500"
                          : ln.tipo === "del"
                          ? "bg-red-50 text-red-800 rounded px-2 py-1 border-l-2 border-red-500 line-through"
                          : "text-slate-700 px-2 py-1"
                      }
                    >
                      <span className="select-none mr-2 font-mono text-xs opacity-70">
                        {ln.tipo === "add" ? "+" : ln.tipo === "del" ? "−" : " "}
                      </span>
                      {ln.texto}
                    </div>
                  ))
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDiffVersionOpen(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Historial/Trazabilidad */}
        <Dialog open={historialOpen} onOpenChange={setHistorialOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Historial de Cambios</DialogTitle>
              <DialogDescription>{procesoActual?.titulo}</DialogDescription>
            </DialogHeader>

            <ScrollArea className="h-96">
              {historialActual.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No hay historial disponible</p>
              ) : (
                <div className="space-y-3">
                  {historialActual.map(h => (
                    <div key={h.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant={h.tipo === 'aprobacion' ? 'default' : h.tipo === 'rechazo' ? 'destructive' : 'outline'}>
                          {getTipoLabel(h.tipo)}
                        </Badge>
                        <span className="text-sm text-slate-500">
                          {new Date(h.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm">
                        <span className="font-medium">{h.user_nombre}</span>
                      </p>
                      {h.detalles?.comentario && (
                        <p className="text-sm text-slate-600 mt-1">"{h.detalles.comentario}"</p>
                      )}
                      {h.caracteres_agregados !== undefined && (
                        <p className="text-xs text-slate-500 mt-1">
                          +{h.caracteres_agregados} / -{h.caracteres_eliminados} caracteres
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <DialogFooter>
              <Button variant="outline" onClick={handleExportarTrazabilidad} data-testid="exportar-trazabilidad-btn">
                <FileDown className="h-4 w-4 mr-2" />
                Exportar Excel
              </Button>
              <Button variant="outline" onClick={() => setHistorialOpen(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Aprobación */}
        <Dialog open={aprobacionOpen} onOpenChange={setAprobacionOpen}>
          <DialogContent className="max-w-[95vw] w-[1100px] max-h-[90vh] overflow-hidden flex flex-col p-0">
            <DialogHeader className="px-6 pt-5 pb-3">
              <DialogTitle>Revisar Cambios</DialogTitle>
              <DialogDescription>
                {aprobacionActual?.proceso_titulo} - Solicitado por {aprobacionActual?.solicitante_nombre}
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 space-y-4">
              {aprobacionActual?.comentario && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Comentario del solicitante:</strong> {aprobacionActual.comentario}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-4 h-4 rounded bg-green-200"></span>
                  <span className="text-slate-600">Texto agregado</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-4 h-4 rounded bg-red-200 line-through"></span>
                  <span className="text-slate-600">Texto eliminado</span>
                </div>
              </div>

              {(aprobacionActual?.contenido_anterior !== undefined || aprobacionActual?.contenido_propuesto !== undefined) ? (
                <DiffWordViewer
                  contenidoAnterior={aprobacionActual?.contenido_anterior || ''}
                  contenidoPropuesto={aprobacionActual?.contenido_propuesto || ''}
                />
              ) : aprobacionActual?.diff_lineas ? (
                <div className="space-y-1 text-sm border rounded-lg p-3" data-testid="aprobacion-diff-lineas">
                  {aprobacionActual.diff_lineas.length === 0 ? (
                    <p className="text-slate-400 italic">Sin cambios de texto detectados.</p>
                  ) : (
                    aprobacionActual.diff_lineas.map((ln, i) => (
                      <div
                        key={i}
                        className={
                          ln.tipo === "add"
                            ? "bg-green-50 text-green-800 rounded px-2 py-1 border-l-2 border-green-500"
                            : ln.tipo === "del"
                            ? "bg-red-50 text-red-800 rounded px-2 py-1 border-l-2 border-red-500 line-through"
                            : "text-slate-700 px-2 py-1"
                        }
                      >
                        <span className="select-none mr-2 font-mono text-xs opacity-70">
                          {ln.tipo === "add" ? "+" : ln.tipo === "del" ? "−" : " "}
                        </span>
                        {ln.texto}
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <DiffViewer 
                  contenidoAnterior={aprobacionActual?.contenido_anterior || ''}
                  contenidoPropuesto={aprobacionActual?.contenido_propuesto || ''}
                />
              )}

              <div className="space-y-2">
                <Label>Comentario (opcional)</Label>
                <Textarea
                  value={comentarioAprobacion}
                  onChange={(e) => setComentarioAprobacion(e.target.value)}
                  placeholder="Añade un comentario para el solicitante..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="px-6 py-4 border-t">
              <Button variant="outline" onClick={() => setAprobacionOpen(false)}>Cancelar</Button>
              <Button 
                variant="destructive" 
                onClick={() => handleProcesarAprobacion(aprobacionActual, 'rechazar')}
              >
                <X className="h-4 w-4 mr-2" />
                Rechazar
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleProcesarAprobacion(aprobacionActual, 'aprobar')}
              >
                <Check className="h-4 w-4 mr-2" />
                Aprobar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

// Componente de trazabilidad global
function TrazabilidadGlobal() {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarHistorial();
  }, []);

  const cargarHistorial = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/procesos-internos/trazabilidad/global?limite=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorial(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      creacion: 'Creación',
      edicion: 'Edición',
      checkout: 'Reservado',
      checkin: 'Liberado',
      version: 'Nueva versión',
      aprobacion: 'Aprobado',
      rechazo: 'Rechazado',
      publicacion: 'Publicado',
      restauracion: 'Restaurado',
      archivado: 'Archivado'
    };
    return labels[tipo] || tipo;
  };

  if (loading) {
    return <div className="flex justify-center py-4"><RefreshCw className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <ScrollArea className="h-64">
      {historial.length === 0 ? (
        <p className="text-center text-slate-500 py-4">No hay historial disponible</p>
      ) : (
        <div className="space-y-2">
          {historial.map(h => (
            <div key={h.id} className="flex items-center gap-3 p-2 border-b last:border-0">
              <Badge variant="outline" className="shrink-0">{getTipoLabel(h.tipo)}</Badge>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">
                  <span className="font-medium">{h.proceso_titulo}</span>
                </p>
                <p className="text-xs text-slate-500">
                  {h.user_nombre} • {new Date(h.timestamp).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  );
}
